import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import ButtonCustom from '@/components/ButtonCustom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { useRootSelector } from '@/hooks/selector.hook';
import { useAuth } from '@/hooks/auth.hook';
import {
  getUserAvatar,
  getUserDisplayName,
  getUserInitial,
  getUserRoleName,
} from '@/utils/user';

import ProfileMenuContent from '../Header/ProfileMenuContent';
import ThemeToggle from '../Helper/ThemeToggle';

export default function SidebarUserCard() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const { logout } = useAuth();
  const { isAuthenticated, user } = useRootSelector((state) => state.auth);

  const displayName = getUserDisplayName(user);
  const roleName = getUserRoleName(user);
  const avatar = getUserAvatar(user);
  const initial = getUserInitial(user);

  const handleConfirmLogout = () => {
    setIsLogoutDialogOpen(false);
    logout();
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-between bg-[#2563EB] px-[1.6rem] py-[1.2rem] lg:hidden">
        <Link
          to="/login"
          className="flex h-[4rem] items-center rounded-[1rem] bg-[#FFFFFF] px-[1.6rem] text-[1.4rem] font-semibold leading-[2rem] text-[#1C37E0]"
        >
          Đăng nhập
        </Link>

        <ThemeToggle />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center bg-[#2563EB] px-[1.6rem] py-[1.2rem] lg:hidden">
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <button type="button" className="flex items-center gap-[1.2rem]">
              <div className="h-[4.6rem] w-[4.6rem] rounded-full border border-[#FFFFFF]">
                <Avatar className="!h-full !w-full">
                  <AvatarImage src={avatar} alt={displayName} />
                  <AvatarFallback className="bg-[#5A8770] text-[1.6rem] font-semibold text-white">
                    {initial}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="flex flex-col gap-[0.4rem] text-left">
                <h4 className="text-[1.4rem] font-medium leading-[2rem] tracking-normal text-[#FFFFFF]">
                  {displayName}
                </h4>
                <p className="text-[1.2rem] font-normal leading-[1.4rem] tracking-normal text-[#FFFFFF]">
                  {roleName}
                </p>
              </div>

              <ChevronDown
                size={18}
                className={`text-[#FFFFFF] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
                  }`}
              />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="mt-[0.8rem] min-w-[18rem] rounded-[1rem] border border-[#E2E8F0] bg-[#FFFFFF] p-[1rem] shadow-[0px_4px_7.6px_0px_#0000001A] dark:border-[#22304A] dark:bg-[#091327] dark:shadow-[0px_12px_28px_0px_rgba(0,0,0,0.35)]"
            align="start"
            collisionPadding={16}
          >
            <ProfileMenuContent
              onLogoutRequest={() => {
                setIsOpen(false);
                setIsLogoutDialogOpen(true);
              }}
              onItemSelect={() => setIsOpen(false)}
            />
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="ml-auto">
          <ThemeToggle />
        </div>
      </div>

      <Dialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen}>
        <DialogContent className="w-[calc(100%-3.2rem)] max-w-[42rem] rounded-[1.2rem] border border-[#E2E8F0] bg-[#FFFFFF] p-[2rem] dark:border-[#22304A] dark:bg-[#091327]">
          <DialogHeader className="space-y-[0.8rem]">
            <DialogTitle className="text-[1.8rem] font-semibold leading-[2.4rem] text-[#212B36] dark:text-[#E9EFFD]">
              Đăng xuất
            </DialogTitle>
            <DialogDescription className="text-[1.4rem] leading-[2rem] text-[#637381] dark:text-[#9FB0D0]">
              Bạn có chắc muốn đăng xuất?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-[2rem] flex-row justify-end gap-[1rem]">
            <ButtonCustom
              type="button"
              onClick={() => setIsLogoutDialogOpen(false)}
              variant="unstyled"
              className="h-[4rem] rounded-[1rem] border border-[#D0D7E2] bg-transparent px-4 text-[#212B36] hover:bg-[#E9EFFD] active:bg-[#D7E3FF] dark:border-[#31415F] dark:text-[#E9EFFD] dark:hover:bg-[#1C37E04D] dark:active:bg-[#1C37E066]"
              title="Hủy"
            >
              Hủy
            </ButtonCustom>

            <ButtonCustom
              type="button"
              variant="unstyled"
              className="h-[4rem] rounded-[1rem] bg-[#2563EB] px-4 text-white hover:bg-[#6D96F2] active:hover:bg-[#1A46A7]"
              onClick={handleConfirmLogout}
            >
              Đăng xuất
            </ButtonCustom>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
